type CellVal = string | boolean | "yes" | "no" | "partial";

interface Row {
  category: string;
  feature: string;
  solar: CellVal;
  gas: CellVal;
  whole: CellVal;
  winner?: "solar" | "gas" | "whole" | "tie";
}

const rows: Row[] = [
  // Fuel & energy
  { category: "Power Source", feature: "Fuel Required", solar: "None / Solar", gas: "Gasoline", whole: "Nat. Gas / Propane", winner: "solar" },
  { category: "Power Source", feature: "Fuel Storage Needed", solar: false, gas: true, whole: false, winner: "tie" },
  { category: "Power Source", feature: "Renewable Energy", solar: true, gas: false, whole: false, winner: "solar" },
  { category: "Power Source", feature: "Unlimited Runtime (with solar)", solar: true, gas: false, whole: "partial", winner: "solar" },
  // Installation
  { category: "Setup", feature: "DIY Setup Possible", solar: true, gas: "partial", whole: false, winner: "solar" },
  { category: "Setup", feature: "Professional Install Required", solar: false, gas: false, whole: true },
  { category: "Setup", feature: "Permit Required", solar: false, gas: false, whole: true },
  { category: "Setup", feature: "Indoor-Safe Use", solar: true, gas: false, whole: false, winner: "solar" },
  // Upfront cost
  { category: "Cost", feature: "Upfront Cost Range", solar: "$500–$5,000", gas: "$300–$2,500", whole: "$5,000–$20,000+" },
  { category: "Cost", feature: "Installation Cost", solar: "Minimal", gas: "Minimal", whole: "$2,000–$5,000" },
  { category: "Cost", feature: "Annual Maintenance Cost", solar: "$0–$50", gas: "$100–$300", whole: "$200–$500" },
  { category: "Cost", feature: "5-Year Total Cost (est.)", solar: "$700–$5,500", gas: "$800–$4,000", whole: "$8,000–$30,000+" },
  // Performance
  { category: "Performance", feature: "Whole-Home Coverage Possible", solar: "partial", gas: "partial", whole: true, winner: "whole" },
  { category: "Performance", feature: "Automatic Failover", solar: false, gas: false, whole: true, winner: "whole" },
  { category: "Performance", feature: "Powers AC Units", solar: "partial", gas: "partial", whole: true },
  { category: "Performance", feature: "Battery Storage Included", solar: true, gas: false, whole: "partial" },
  { category: "Performance", feature: "Expandable Capacity", solar: true, gas: false, whole: false, winner: "solar" },
  // Environment & convenience
  { category: "Usability", feature: "Noise Level", solar: "Silent", gas: "Loud", whole: "Low" },
  { category: "Usability", feature: "Zero Emissions", solar: true, gas: false, whole: false, winner: "solar" },
  { category: "Usability", feature: "Portable", solar: true, gas: true, whole: false },
  { category: "Usability", feature: "App / Remote Monitoring", solar: true, gas: false, whole: "partial", winner: "solar" },
  { category: "Usability", feature: "Maintenance Frequency", solar: "Low", gas: "Regular", whole: "Annual Professional" },
  // Best for
  { category: "Ideal Use", feature: "Emergency Preparedness", solar: true, gas: "partial", whole: true },
  { category: "Ideal Use", feature: "Off-Grid Living", solar: true, gas: false, whole: false, winner: "solar" },
  { category: "Ideal Use", feature: "Short Outages (1–8 hrs)", solar: true, gas: true, whole: true },
  { category: "Ideal Use", feature: "Extended Outages (3–14 days)", solar: true, gas: "partial", whole: true },
  { category: "Ideal Use", feature: "Whole-Home Seamless Power", solar: false, gas: false, whole: true, winner: "whole" },
];

function Cell({ value }: { value: CellVal }) {
  if (value === true) {
    return (
      <span className="inline-flex w-6 h-6 items-center justify-center bg-emerald-100 rounded-full mx-auto">
        <i className="ri-check-line text-emerald-600 text-sm"></i>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex w-6 h-6 items-center justify-center bg-red-50 rounded-full mx-auto">
        <i className="ri-close-line text-red-400 text-sm"></i>
      </span>
    );
  }
  if (value === "partial") {
    return (
      <span className="inline-flex w-6 h-6 items-center justify-center bg-amber-50 rounded-full mx-auto">
        <i className="ri-subtract-line text-amber-500 text-sm"></i>
      </span>
    );
  }
  return <span className="text-sm text-gray-700 whitespace-nowrap">{value}</span>;
}

export default function MasterComparisonTable() {
  const categories = [...new Set(rows.map((r) => r.category))];

  return (
    <section id="comparison-table" className="py-20 bg-gray-50 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-amber-500 text-xs font-bold uppercase tracking-widest">Side-by-Side</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2 mb-3">
            Full Specification Comparison
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            26 criteria across fuel, cost, performance, safety, and real-world use — no marketing spin.
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-5 justify-center text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="inline-flex w-5 h-5 items-center justify-center bg-emerald-100 rounded-full"><i className="ri-check-line text-emerald-600 text-xs"></i></span> Yes / Advantage
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex w-5 h-5 items-center justify-center bg-red-50 rounded-full"><i className="ri-close-line text-red-400 text-xs"></i></span> No / Disadvantage
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex w-5 h-5 items-center justify-center bg-amber-50 rounded-full"><i className="ri-subtract-line text-amber-500 text-xs"></i></span> Partial / Depends
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 bg-white">
                <th className="py-4 px-5 text-left text-gray-400 font-semibold text-xs uppercase tracking-wider w-52">Feature</th>
                <th className="py-4 px-5 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-full">
                      <i className="ri-sun-line text-amber-500 text-lg"></i>
                    </div>
                    <span className="font-extrabold text-amber-700 text-sm">Solar Generator</span>
                    <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold">Our Pick</span>
                  </div>
                </th>
                <th className="py-4 px-5 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-full">
                      <i className="ri-oil-line text-gray-500 text-lg"></i>
                    </div>
                    <span className="font-extrabold text-gray-700 text-sm">Gas Generator</span>
                  </div>
                </th>
                <th className="py-4 px-5 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded-full">
                      <i className="ri-building-2-line text-slate-500 text-lg"></i>
                    </div>
                    <span className="font-extrabold text-gray-700 text-sm">Whole Home Standby</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const catRows = rows.filter((r) => r.category === cat);
                return catRows.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-gray-100 transition-colors hover:bg-amber-50/30 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                    {i === 0 && (
                      <td
                        rowSpan={catRows.length}
                        className="py-4 px-5 align-top"
                      >
                        <span className="inline-block bg-gray-100 text-gray-500 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap">
                          {cat}
                        </span>
                      </td>
                    )}
                    {i !== 0 && null}
                    <td className="py-3 px-5 font-medium text-gray-700 text-sm whitespace-nowrap" style={i === 0 ? {} : {}}>
                      {i !== 0 && <span className="sr-only">{cat}</span>}
                      {row.feature}
                    </td>
                    <td className="py-3 px-5 text-center bg-amber-50/30">
                      <div className="flex justify-center">
                        <Cell value={row.solar} />
                      </div>
                    </td>
                    <td className="py-3 px-5 text-center">
                      <div className="flex justify-center">
                        <Cell value={row.gas} />
                      </div>
                    </td>
                    <td className="py-3 px-5 text-center">
                      <div className="flex justify-center">
                        <Cell value={row.whole} />
                      </div>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>

        <p className="text-center text-gray-400 text-xs mt-5">
          Based on independent testing, manufacturer specifications, and real-world use across EcoFlow, Renogy, and Nature&apos;s Generator product lines.
        </p>
      </div>
    </section>
  );
}
